import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  ViewChild,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

import { BaseResizeComponent } from './base.resize.component';
import { RadioButtonComponent } from './sub-group/radioButton/radio-button.component';
import {
  ISelection,
  ISubgroupMode,
  IToggleProperty,
  SubgroupComponent,
  UserSelection,
} from './sub-group/sub-group.component';
import { AxpoButtonComponent } from '../../core/axpo-button/axpo-button.component';
import { AxpoDialogComponent } from '../../core/axpo-dialog/axpo-dialog.component';
import {
  AxpoFormElementComponent,
  IFormError,
  IOption,
} from '../../core/axpo-form-element/axpo-form-element.component';
import { AxpoSpinnerComponent } from '../../core/axpo-spinner/axpo-spinner.component';
import { AxpoTypographyComponent } from '../../core/axpo-typography/axpo-typography.component';
import { LocationAutocompleteComponent } from '../../shared/controls/location/location-autocomplete/location-autocomplete.component';
import { ITabItem, TabsComponent } from '../../shared/controls/tabs/tabs.component';
import {
  Circle,
  ICircleContact,
  IOnCall,
  IUpdateCircle,
  IUserState,
  Organization,
  Subgroup,
  User,
  UserPhoneNumbers,
} from '../../shared/models/api_models';
import { IAutocompleteEntry } from '../../shared/models/component_models';
import { CircleService } from '../../shared/services/circle.service';
import { FormDebounceService } from '../../shared/services/form-debounce.service';
import { LanguageService } from '../../shared/services/lang.service';
import {
  DIALOG_EVENTS,
  OnSiteDestinationId,
  REDIRECT_USER_ID,
  Roles,
  SCROLL_DEBOUNCE_MS,
} from '../../shared/utils/constants';
import { FormValidator } from '../../shared/utils/formValidator';

export type actionToPerform =
  'back' | 'toggleManageContacts' | 'toggleManageCalls' | 'cancel' | 'openPhoneDialog';
export type circleMode = 'overview' | 'circle';

@Component({
  selector: 'app-circles',
  imports: [
    SubgroupComponent,
    AxpoTypographyComponent,
    AxpoButtonComponent,
    AxpoDialogComponent,
    AxpoFormElementComponent,
    TabsComponent,
    TranslocoPipe,
    NgClass,
    LocationAutocompleteComponent,
    AxpoSpinnerComponent,
    RadioButtonComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './circles.component.html',
  styleUrls: ['./circles.component.css'],
})
export class CirclesComponent extends BaseResizeComponent {
  private activatedRoute = inject(ActivatedRoute);
  private debounceService = inject(FormDebounceService);
  private translocoService = inject(TranslocoService);
  private router = inject(Router);
  circleService = inject(CircleService);
  langService = inject(LanguageService);
  circle = signal<Circle | undefined>(undefined);
  subgroups = signal<Subgroup[] | undefined>(undefined);
  selectedUser = signal<UserSelection>({});
  circleModeBtn = signal<IToggleProperty>('onCall');
  onSiteSubgroup = signal<Subgroup | undefined>(undefined);
  addNewContactDialog = signal<boolean>(false);
  formManager: FormValidator | undefined;
  newContactSubwork = signal<number | undefined>(undefined);
  editedContact = signal<ICircleContact | undefined>(undefined);
  isManageContactsAllowed = signal<boolean>(false);
  circleTabs = signal<ITabItem[]>([]);
  subgroupOptions = signal<IOption[]>([]);
  onSite = signal<Subgroup | undefined>(undefined);
  answeringMachine = signal<Subgroup | undefined>(undefined);
  contacts = signal<User[] | null | undefined>(undefined);
  filterContactSearch = signal<string>('');
  selectedSearchResults = signal<IAutocompleteEntry[]>([]);
  searchQueryResult = signal<IAutocompleteEntry[]>([]);
  abortDialogSaving = signal<boolean>(false);
  activeUsersChanged = signal<IUserState[]>([]);
  onCallChanged = signal<IOnCall[]>([]);
  mainNumberChanged = signal<number | null | undefined>(undefined);

  @ViewChild('stickyContainer', { static: false })
  stickyContainer!: ElementRef<HTMLDivElement>;
  isStuck = signal<boolean>(false);
  localUserNumbers = signal<UserPhoneNumbers | undefined>(undefined);
  customPhoneNumber = signal<string>('');
  showEditNumberDialog = signal<boolean>(false);
  phoneFormManager: FormValidator | undefined;
  private scrollDebounceTimeout: number | null = null;

  // ToDo: maintanance use mainNumberChanged to handle and object, not only the id
  // To include OnSiteDestinationType
  selectionMode = signal<ISubgroupMode>('radio');
  circleWithOfficePhone = signal<boolean>(true);

  // controls to manage the state of the component externally
  mode = input<circleMode>('circle');
  circleId = input<number>(0);
  selectedTab = input<IToggleProperty | undefined>(undefined);
  allDisabled = input<boolean>(false);

  // allow tabs to be set from the outside
  _tab = effect(() => {
    const tab = this.selectedTab();
    const availableTabs = this.circleTabs();

    if (tab) {
      const tabItem = availableTabs.find(t => t.id === tab);
      if (tabItem) {
        this.tabSelectionChanged(tabItem);
      }
    }
  });

  _effect = effect(() => {
    const customPhoneNumber = this.customPhoneNumber();

    untracked(() => {
      this.localUserNumbers.update(current => {
        if (!current) return current;

        return {
          ...current,
          customPhoneNumber: customPhoneNumber
            ? {
                active: current.customPhoneNumber?.active ?? false,
                phoneNumber: customPhoneNumber,
              }
            : undefined,
        };
      });
      if (
        (customPhoneNumber === undefined || customPhoneNumber === '') &&
        this.localUserNumbers()?.defaultPhoneNumber
      ) {
        this.defaultNumberSelected();
      }

      this.validatePhoneNumbersForm();
    });
  });

  @HostListener('window:scroll', [])
  onWindowScroll() {
    if (this.scrollDebounceTimeout !== null) {
      window.clearTimeout(this.scrollDebounceTimeout);
    }
    this.scrollDebounceTimeout = window.setTimeout(() => {
      this.setStuckState();
      this.scrollDebounceTimeout = null;
    }, SCROLL_DEBOUNCE_MS);
  }

  setStuckState() {
    if (this.stickyContainer?.nativeElement && this.mode() === 'circle') {
      const rect = this.stickyContainer.nativeElement.getBoundingClientRect();
      const isCurrentlyStuck = rect.bottom === window.innerHeight;
      // Means the section is stuck at the page and not in the original position
      this.isStuck.set(isCurrentlyStuck);
    }
  }

  getRedirectUser = () => {
    return {
      id: 0,
      first_name: '',
      last_name: '',
    };
  };

  selectRedirect = (user: User, organization: Organization) => {
    this.updateUserOnCall(
      {
        subgroupId: 0,
        groupId: organization.id?.toString() || '',
        user: user,
        property: 'onCall',
      },
      organization.id,
    );
  };

  _userNumbers = effect(() => {
    const userNumbers = this.circleService.userNumbers();

    untracked(() => {
      this.localUserNumbers.set(userNumbers);
      this.customPhoneNumber.set(userNumbers?.customPhoneNumber?.phoneNumber || '');
    });
  });

  _userUpdated = effect(() => {
    const updated = this.circleService.updatingUserState();
    if (updated) {
      this.activeUsersChanged.set([]);
    }
  });

  _circleRenderEffect = effect(() => {
    const _localCircle = this.circle();
    untracked(() => {
      setTimeout(() => {
        // delay to allow circle to render so the height calculation is correct
        this.setStuckState();
      }, 50);
    });
  });

  _circles = effect(() => {
    const _languageChange = this.circleService.languageChange();
    const circle = this.circleService.circle();
    const allSubgroups = circle?.organizations?.flatMap((org: Organization) => org.subgroups || []);
    if (circle && circle.id == this.circleId()) {
      untracked(() => {
        this.circleWithOfficePhone.set(!!circle.mainNumber);
        circle?.organizations?.sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
        this.circle.set(circle);
        this.onSite.set(circle?.onSite);
        this.answeringMachine.set(circle?.answeringMachine);
        this.subgroups.set(allSubgroups);
        if (allSubgroups) {
          this.subgroupOptions.set(
            allSubgroups.map((sg: Subgroup) => {
              return {
                value: sg.id?.toString(),
                label: this.translocoService.translate(
                  'switchboard.subgroupNames.' + (sg.name?.replaceAll(' ', '') ?? ''),
                ),
              };
            }) as IOption[],
          );
        }
      });
    }
  });

  _tabChange = effect(() => {
    const tab = this.circleModeBtn();
    if (tab !== 'activeOnCall' && tab !== 'activeOnSite') {
      this.selectionMode.set('radio');
    } else {
      this.selectionMode.set('switch');
    }
  });

  _isAdminContacts = effect(() => {
    const circleId = this.circleId();
    const userRoles = this.circleService.userInfo()?.sb_roles || [];
    const isManageContactsAllowed = [Roles.SB_MASTER_ADMIN, Roles.SB_CIRCLE_ADMIN].some(role =>
      userRoles.includes(role),
    );

    untracked(() => {
      if (
        !this.circleService.loadingCircle() &&
        (!this.circle() || this.circle()?.id != circleId)
      ) {
        this.circleService.getCircle(circleId);
      }

      this.isManageContactsAllowed.set(isManageContactsAllowed);
    });
  });

  _createTabs = effect(() => {
    const _circleWithOfficePhone = this.circleWithOfficePhone();

    untracked(() => {
      this.circleTabs.set(this._getCircleTabs());
    });
  });

  // Note: Circle change tab reset logic is handled in _circles effect (isFirstLoadOrCircleChange)

  _nameValidationEffect = effect(() => {
    const results = this.selectedSearchResults();
    const name = results
      .map(elem => {
        return elem.title;
      })
      .join('');

    untracked(() => {
      this.formManager?.checkRequired('name', name);
    });
  });

  _subworkValidationEffect = effect(() => {
    const subwork = this.newContactSubwork();

    untracked(() => {
      this.formManager?.checkRequired('subwork', subwork);

      if (subwork) {
        this.selectedSearchResults.set([]);
      }
    });
  });

  _updateSearchResults = effect(() => {
    // exclude users of selected subwork - only activate after subwork was selected
    const searchResults = this.circleService.searchedUsers();
    if (searchResults) {
      const contactsFound = searchResults
        .map(result => {
          return {
            id: result.id ?? -1,
            title: result.first_name + ' ' + result.last_name,
            subtitle: result.role,
            value: result.id,
          };
        })
        .filter(result => {
          const organization = this.circle()?.organizations?.find((org: Organization) => {
            return org.subgroups.find((subgroup: Subgroup) => {
              return subgroup.id === Number(this.newContactSubwork());
            });
          });

          const subgroup = organization?.subgroups.find((subgroup: Subgroup) => {
            return subgroup.id === Number(this.newContactSubwork());
          });

          const user = subgroup?.users?.find((user: User) => {
            return user.id === result.id;
          });

          return !user;
        });
      this.searchQueryResult.set(contactsFound);
    }
  });

  _updatedCircle = effect(() => {
    const updatedCircle = this.circleService.updatedCircle();
    if (updatedCircle) {
      untracked(() => {
        this.onCallChanged.set([]);
        this.mainNumberChanged.set(undefined);
      });
    }
  });

  _clearAllUsersOnSiteStatus() {
    this.circle()?.organizations?.forEach(org => {
      org.subgroups?.forEach(subgroup => {
        subgroup.users?.forEach(user => {
          user.isOnSite = false;
        });
      });
    });
  }

  _getCircleTabs = () => {
    let circleTabs: ITabItem[] = [];
    if (this.circleWithOfficePhone()) {
      circleTabs = [
        { id: 'onCall', title: 'switchboard.onCall' },
        { id: 'isOnSite', title: 'switchboard.onSite' },
      ];
    } else {
      circleTabs = [{ id: 'onCall', title: 'switchboard.onCall' }];
    }
    return circleTabs;
  };

  _getCircleManageContactTabs = () => {
    let circleTabs: ITabItem[] = [];
    const circleWithOfficePhone = this.circle()?.mainNumber;
    if (circleWithOfficePhone) {
      circleTabs = [
        { id: 'activeOnCall', title: 'switchboard.manageContactsOnCall' },
        { id: 'activeOnSite', title: 'switchboard.manageContactsOnSite' },
      ];
    } else {
      circleTabs = [{ id: 'activeOnCall', title: 'switchboard.manageContactsOnCall' }];
    }
    return circleTabs;
  };

  addContact() {
    this.formManager = new FormValidator(
      [
        {
          key: 'subwork',
          errorMessageSignal: signal(undefined),
          errorIdSignal: signal(undefined),
        },
        {
          key: 'name',
          errorMessageSignal: signal(undefined),
          errorIdSignal: signal(undefined),
        },
      ],
      this.translocoService,
      this.debounceService,
    );

    this.validateForm();
    this.addNewContactDialog.set(true);
  }

  toggleManageContacts() {
    this.selectionMode.set('switch');
    this.circleModeBtn.set('activeOnCall' as IToggleProperty);
    this.circleTabs.set(this._getCircleManageContactTabs());
  }

  toggleManageCalls() {
    this.selectionMode.set('radio');
    this.circleModeBtn.set('onCall' as IToggleProperty);
    this.circleTabs.set(this._getCircleTabs());
  }

  back() {
    if (this.circleModeBtn() === 'onCall' || this.circleModeBtn() === 'isOnSite') {
      this.router.navigate(['/']);
    } else {
      this.toggleManageCalls();
    }
  }
  actionToPerform = signal<string | undefined>(undefined);

  saveDialogActionDispatcher(dialogAction: actionToPerform) {
    switch (dialogAction) {
      case 'cancel': {
        this.actionToPerform.set(undefined);
        break;
      }
      case 'toggleManageCalls': {
        this.toggleManageCalls();
        break;
      }
      case 'toggleManageContacts': {
        this.toggleManageContacts();
        break;
      }
      case 'openPhoneDialog': {
        this.openCustomNumberModal();
        break;
      }
      case 'back': {
        this.back();
        break;
      }
    }
  }

  resetState() {
    this.onCallChanged.set([]);
    this.mainNumberChanged.set(undefined);
    this.activeUsersChanged.set([]);
    this.actionToPerform.set(undefined);
    this.circleService.getCircle(this.circleId());
  }

  checkForUnsavedChangesElsePerformAction(actionToPerform: actionToPerform) {
    if (this.showSaveDialog) {
      this.actionToPerform.set(actionToPerform);
    } else {
      this.saveDialogActionDispatcher(actionToPerform);
    }
  }

  validateForm() {
    this.formManager?.checkRequired('subwork', this.newContactSubwork());
    this.formManager?.checkRequired(
      'name',
      this.selectedSearchResults()
        .map(elem => {
          return elem.title;
        })
        .join(''),
    );
  }

  contactDialogClosed(action: any) {
    this.abortDialogSaving.set(false);
    if (action === 'cancel') {
      this.addNewContactDialog.set(false);
      this.editedContact.set(undefined);
      this.newContactSubwork.set(undefined);
      this.selectedSearchResults.set([]);
      return;
    }

    this.validateForm();
    if (!this.formManager?.showErrorOnLoad()) {
      this.formManager?.showErrors();
    }

    if (!this.formManager?.isValid) {
      this.abortDialogSaving.set(true);
      return;
    }

    const subworkId = this.newContactSubwork();
    const addUsers = this.selectedSearchResults();

    if (subworkId && addUsers && addUsers.length > 0) {
      this.circleService.updateCircle(this.circleId(), {
        main_number_user_id: undefined,
        add_users: addUsers.map(user => {
          return {
            subgroup_id: Number(subworkId),
            user_id: Number(user.id),
          };
        }),
        remove_users: undefined,
        on_call: undefined,
      });

      this.addNewContactDialog.set(false);
      this.editedContact.set(undefined);
      this.newContactSubwork.set(undefined);
      this.selectedSearchResults.set([]);
    }
  }

  formValidation(formError: IFormError | undefined, key: string) {
    const field = this.formManager?.getField(key);
    const fieldErrorId = field?.errorId();

    if (formError) {
      this.formManager?.setError(
        formError.formId,
        formError.value,
        formError.message,
        formError.replacement,
      );
    } else if (fieldErrorId && fieldErrorId > -1) {
      this.formManager?.clearError(key);
    }
  }

  beforeMedian = (organizationSubgroups: Subgroup[]) => {
    if (organizationSubgroups) {
      const median =
        organizationSubgroups.length % 2 === 0
          ? organizationSubgroups.length / 2
          : organizationSubgroups.length / 2 + 1;
      if (organizationSubgroups.length > median) {
        return organizationSubgroups.sort(this.sortSubgroups).slice(0, median);
      }

      return organizationSubgroups.sort(this.sortSubgroups);
    }

    return [];
  };

  afterMedian = (organizationSubgroups: Subgroup[]) => {
    if (organizationSubgroups) {
      const median =
        organizationSubgroups.length % 2 === 0
          ? organizationSubgroups.length / 2
          : organizationSubgroups.length / 2 + 1;
      if (organizationSubgroups.length > median) {
        return organizationSubgroups.slice(median);
      }
    }

    return [];
  };

  sortSubgroups(a: Subgroup, b: Subgroup): number {
    return (a.id ?? 0) - (b.id ?? 0);
  }

  updateUserOnCall(selection: ISelection, organizationId: number) {
    const onCallIndex = this.onCallChanged().findIndex(us => {
      return us.organization_id === organizationId;
    });
    const onCall =
      selection.user.id === REDIRECT_USER_ID
        ? { organization_id: organizationId, redirect: true }
        : { organization_id: organizationId, user_id: selection.user.id };

    if (onCallIndex > -1) {
      this.onCallChanged.update((onCallList: IOnCall[]) => {
        onCallList[onCallIndex] = onCall;
        return onCallList;
      });
    } else {
      this.onCallChanged.update((onCallList: IOnCall[]) => {
        onCallList.push(onCall);
        return onCallList;
      });
    }
  }

  updateUserOnSite(selection: ISelection) {
    let userId = selection?.user?.id > 0 ? selection?.user?.id : null;
    if (selection?.user?.id === OnSiteDestinationId.ANSWERING_MACHINE) {
      userId = OnSiteDestinationId.ANSWERING_MACHINE;
    }

    this.onSite()?.users?.forEach(user => {
      user.isOnSite = selection?.user?.id === OnSiteDestinationId.OFFICE_PHONE;
    });
    this.answeringMachine()?.users?.forEach(user => {
      user.isOnSite = selection?.user?.id === OnSiteDestinationId.ANSWERING_MACHINE;
    });

    if (!userId || userId === OnSiteDestinationId.ANSWERING_MACHINE) {
      this._clearAllUsersOnSiteStatus();
    }

    this.mainNumberChanged.set(userId);
  }

  updateActive(selection: ISelection) {
    this.activeUsersChanged.update(activeUsers => {
      const index = activeUsers.findIndex(item => item.subgroup_id === selection.subgroupId);
      if (index !== -1) {
        // update subgroup
        const userIndex = activeUsers[index].users.findIndex(us => {
          return us.user_id === selection.user.id;
        });
        if (userIndex > -1) {
          activeUsers[index].users[userIndex].activeOnCall = !!selection.user.activeOnCall;
          activeUsers[index].users[userIndex].activeOnSite = !!selection.user.activeOnSite;
        } else {
          activeUsers[index].users.push({
            user_id: selection.user.id,
            activeOnCall: !!selection.user.activeOnCall,
            activeOnSite: !!selection.user.activeOnSite,
          });
        }
      } else {
        // add subgroup including user
        activeUsers.push({
          subgroup_id: selection.subgroupId,
          users: [
            {
              user_id: selection.user.id,
              activeOnCall: !!selection.user.activeOnCall,
              activeOnSite: !!selection.user.activeOnSite,
            },
          ],
        });
      }
      return activeUsers;
    });
  }

  onUserChanged = (selection: ISelection) => {
    this.selectedUser.update(userSelection => {
      userSelection[selection.groupId] = selection.user;

      return userSelection;
    });

    if (selection.property === 'isOnSite') {
      this.updateUserOnSite(selection);
    } else if (selection.property === 'onCall') {
      const selectionGroup = selection.groupId;
      const selectedId = Number(selectionGroup.split('_')[1]);
      this.updateUserOnCall(selection, selectedId);
    } else {
      this.updateActive(selection);
    }
  };

  getSelectionGroupId(organizationId?: number) {
    const setId = this.circleId() || this.activatedRoute.snapshot.paramMap.get('id');
    if (this.circleModeBtn() === 'isOnSite') {
      return 'circle_' + setId;
    }

    return 'organization_' + organizationId;
  }

  save() {
    let circleUpdate: IUpdateCircle = {
      main_number_user_id: this.mainNumberChanged(),
      on_call: this.onCallChanged(),
    };

    if (this.mainNumberChanged() === OnSiteDestinationId.ANSWERING_MACHINE) {
      circleUpdate = {
        answering_machine: true,
        on_call: this.onCallChanged(),
      };
    } else if (this.mainNumberChanged() === null) {
      circleUpdate = {
        office_phone: true,
        on_call: this.onCallChanged(),
      };
    }

    this.circleService.updateCircle(this.circleId(), circleUpdate);
  }

  tabSelectionChanged(tab: ITabItem | undefined) {
    this.circleModeBtn.set(tab?.id as IToggleProperty);
  }

  searchValueChanged(searchterm: string | undefined) {
    if (searchterm && searchterm.length > 2) {
      this.circleService.searchUser(searchterm as string);
    }
  }

  searchEntrySelected(entry: IAutocompleteEntry) {
    this.selectedSearchResults.update(results => {
      results.push(entry);

      return results;
    });
  }

  searchEntryRemoved(entry: IAutocompleteEntry) {
    this.selectedSearchResults.update(results => {
      results.splice(results.indexOf(entry), 1);

      return results;
    });
  }

  saveManageContacts() {
    this.circleService.setUserState(this.activeUsersChanged(), this.circleId());
  }

  get isDeleteManuallyAddedAllowed() {
    return (
      this.isManageContactsAllowed() &&
      (this.circleModeBtn() === 'activeOnCall' || this.circleModeBtn() === 'activeOnSite')
    );
  }

  get onCallOrOnSiteChange(): boolean {
    return this.onCallChanged().length === 0 && this.mainNumberChanged() === undefined;
  }

  get activeUsersChange(): boolean {
    return this.activeUsersChanged().length === 0;
  }

  get showSaveDialog(): boolean {
    return !this.onCallOrOnSiteChange || !this.activeUsersChange;
  }

  openCustomNumberModal() {
    this.showEditNumberDialog.set(true);
    this.phoneFormManager = new FormValidator(
      [
        {
          key: 'defaultNumber',
          errorMessageSignal: signal(undefined),
          errorIdSignal: signal(undefined),
        },
        {
          key: 'customNumber',
          errorMessageSignal: signal(undefined),
          errorIdSignal: signal(undefined),
        },
      ],
      this.translocoService,
      this.debounceService,
    );

    this.validatePhoneNumbersForm();
    this.circleService.getUserNumbers();
  }

  validatePhoneNumbersForm() {
    if (!this.isValidPhoneFormat(this.customPhoneNumber())) {
      this.phoneFormManager?.setError('customNumber', 2, '');
      return;
    }

    this.phoneFormManager?.clearError('customNumber');
  }

  private isValidPhoneFormat(phoneNumber: string): boolean {
    if (!phoneNumber) return true; // Empty phone number is considered valid
    // Regular expression to match only digits (0-9), plus sign (+), and spaces
    const phoneRegex = /^[\d+\s]*$/;
    return phoneRegex.test(phoneNumber);
  }

  numberDialogClosed(event: any) {
    if (event === DIALOG_EVENTS.CONFIRM) {
      this.circleService.updateUserNumbers({
        defaultPhoneNumber: this.localUserNumbers()
          ?.defaultPhoneNumber as UserPhoneNumbers['defaultPhoneNumber'],
        customPhoneNumber: this.localUserNumbers()?.customPhoneNumber,
      });
    }

    this.showEditNumberDialog.set(false);
  }

  customNumberSelected() {
    this.localUserNumbers.update(current => {
      if (!current) return current;

      return {
        ...current,
        defaultPhoneNumber: current.defaultPhoneNumber
          ? {
              ...current.defaultPhoneNumber,
              active: false,
            }
          : undefined,
        customPhoneNumber: current.customPhoneNumber
          ? {
              ...current.customPhoneNumber,
              active: true,
            }
          : undefined,
      };
    });
  }

  defaultNumberSelected() {
    this.localUserNumbers.update(current => {
      if (!current) return current;

      return {
        ...current,
        defaultPhoneNumber: current.defaultPhoneNumber
          ? {
              ...current.defaultPhoneNumber,
              active: true,
            }
          : undefined,
        customPhoneNumber: current.customPhoneNumber
          ? {
              ...current.customPhoneNumber,
              active: false,
            }
          : undefined,
      };
    });
  }

  handleSaveBeforeOpeningPhoneDialos() {
    if (this.circleModeBtn() === 'onCall' || this.circleModeBtn() === 'isOnSite') this.save();
    else if (this.circleModeBtn() === 'activeOnCall' || this.circleModeBtn() === 'activeOnSite')
      this.saveManageContacts();

    this.openCustomNumberModal();
    this.actionToPerform.set(undefined);
  }
  //ToDo Refactor customNumberSelected and defaultNumberSelected into one method
}
